import { defer, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { AppContext } from './app-context';
import { GatewayCondition } from './gateway-connection';
import { NodeRequest, NodeRequestOptions } from './node-connection';

export interface NodeExtensionInstallResult extends GatewayCondition {
    response: any;
}

/**
 * Node extension install class.
 */
export class NodeExtensionInstall {
    private static installUrl = 'features/extensions/{0}/versions/{1}/install';

    /**
     * Initializes a new instance of the NodeExtensionInstall class.
     *
     * @param appContext the application context.
     */
    constructor(private appContext: AppContext) { }

        /**
     * Install a node package of extensions. (elevate the desktop gateway if required.)
     *
     * @param nodeName the name of node.
     * @param extension the name of extension.
     * @param version the version of extension.
     * @param options the node request options (optional).
     */
    public install(nodeName: string, extension: string, version: string, options?: NodeRequestOptions): Observable<NodeExtensionInstallResult> {
        const install = defer(() => this.installCall(nodeName, extension, version, options));
        let result: NodeExtensionInstallResult = null;
        return this.appContext.gateway.checkCondition()
            .pipe(
                mergeMap(data => {
                    result = <NodeExtensionInstallResult>data;
                    if (result.isServiceMode) {
                        if (result.isGatewayAdmin) {
                            return install;
                        }

                        // cannot support this user.
                        return of(null);
                    }

                    if (result.isGatewayProcessElevated) {
                        return install;
                    }

                    // on-demand elevate and install.
                    return this.appContext.gateway.elevate()
                        .pipe(switchMap(elevated => {
                            if (elevated) {
                                result.isGatewayProcessElevated = true;
                                return install;
                            }

                            // user rejected to elevate.
                            return of(result);
                        }));
                }),
                map(installed => {
                    result.response = installed;
                    return result;
                })
            );
    }

    /**
     * Install a node package of extensions.(doesn't handle elevation for the desktop mode.)
     *
     * @param nodeName the name of node.
     * @param extension the name of extension.
     * @param version the version of extension.
     * @param options the node request options (optional).
     */
    private installCall(nodeName: string, extension: string, version: string, options?: NodeRequestOptions): Observable<any> {
        const url = NodeExtensionInstall.installUrl.format(extension, version);
        return this.appContext.node.post(nodeName, url, null, <NodeRequest>options);
    }
}
